import LiquidText from '../components/LiquidText';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import PageThumbnails from '../components/MaqueteEditor/PageThumbnails';
import ElementsLibrary from '../components/MaqueteEditor/ElementsLibrary';
import EditorCanvas from '../components/MaqueteEditor/EditorCanvas';
import PropertiesPanel from '../components/MaqueteEditor/PropertiesPanel';
import './MaquetesSimple.css';
import { useMaquetesTheme } from '../context/MaquetesThemeContext';
import MaquetesObbaDashboard from '../components/MaquetesObbaDashboard';

const MaquetesSimple = () => {
    const { active: obbaTheme } = useMaquetesTheme();
    const [dashboardError, setDashboardError] = useState(false);
    const { obraId } = useParams();
    const navigate = useNavigate();
    const [dashboardObras, setDashboardObras] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [pages, setPages] = useState([]);
    const [currentPageIdx, setCurrentPageIdx] = useState(0);
    const [elements, setElements] = useState([]);
    const [selectedElementId, setSelectedElementId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [availableSections, setAvailableSections] = useState([]);
    const [obraData, setObraData] = useState(null);

    useEffect(() => {
        if (!obraId) {
            loadDashboard();
        } else {
            loadObraMaquete();
        }
        loadTemplates();
        loadAllSections();
    }, [obraId]);

    const loadAllSections = async () => {
        try {
            const res = await api.get('/maquetes/sections');
            setAvailableSections(res.data);
        } catch (error) {
            console.error('Erro ao carregar secções:', error);
        }
    };

    const loadDashboard = async () => {
        setLoading(true);
        setDashboardError(false);
        try {
            const res = await api.get('/maquetes/dashboard-obras');
            setDashboardObras(res.data);
        } catch (error) {
            setDashboardError(true);
            console.error('Erro ao carregar dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadObraMaquete = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/maquetes/obra/${obraId}`);
            const { maquete, elements: elms, sections: secs } = res.data;

            setSelectedTemplateId(maquete.template_id);

            // Configurar páginas/secções
            const configuredPages = secs
                .sort((a, b) => a.ordem - b.ordem)
                .map(s => ({
                    section_id: s.section_id,
                    name: (s.section_name === 'Capa' || s.ordem === 0) ? 'PLANO IMAGEM' : s.section_name,
                    ordem: s.ordem,
                    asset_url: s.asset_url
                }));
            setPages(configuredPages);

            // Configurar elementos
            const pageElements = elms.map(el => {
                let styles = {};
                try {
                    styles = typeof el.styles === 'string' ? JSON.parse(el.styles) : (el.styles || {});
                } catch (e) {
                    console.error("Erro ao processar estilos do elemento:", e);
                }
                return { ...el, styles };
            });
            setElements(pageElements);
            setCurrentPageIdx(0);

            // Carregar dados da obra para o título
            const obraRes = await api.get(`/obras/${obraId}`);
            setObraData(obraRes.data.obra);
        } catch (error) {
            console.error('Erro ao carregar maquete da obra:', error);
            const msg = error.response?.data?.error || 'Erro ao carregar maquete.';
            const details = error.response?.data?.details || 'Verifique se existem templates criados.';
            alert(`${msg}\n\n${details}`);
        } finally {
            setLoading(false);
        }
    };

    const loadTemplates = async () => {
        try {
            const res = await api.get('/maquetes/templates');
            setTemplates(res.data);
        } catch (error) {
            console.error('Erro ao carregar templates:', error);
        }
    };

    const loadTemplatePages = async () => {
        try {
            const res = await api.get(`/maquetes/templates/${selectedTemplateId}/sections`);

            // Garantir que temos as secções necessárias, filtrando as ativas
            const configuredPages = res.data
                .filter(s => s.ativo)
                .sort((a, b) => {
                    // Capa (ordem 0 ou nome 'Capa') sempre primeiro
                    if (a.section_name === 'Capa') return -1;
                    if (b.section_name === 'Capa') return 1;
                    return a.ordem - b.ordem;
                })
                .map(s => ({
                    section_id: s.section_id,
                    name: s.section_name,
                    ordem: s.ordem,
                    asset_url: s.asset_url
                }));

            setPages(configuredPages);
            setCurrentPageIdx(0);
        } catch (error) {
            console.error('Erro ao carregar páginas:', error);
        }
    };

    const loadPageElements = async () => {
        setLoading(true);
        try {
            const currentPage = pages[currentPageIdx];
            if (!currentPage) {
                setElements([]);
                return;
            }

            const res = await api.get(`/maquetes/templates/${selectedTemplateId}`);
            const pageElements = res.data.elements
                .filter(el => el.section_id === currentPage.section_id)
                .map(el => {
                    let styles = {};
                    try {
                        styles = typeof el.styles === 'string' ? JSON.parse(el.styles) : (el.styles || {});
                    } catch (e) {
                        console.error("Erro ao processar estilos do elemento:", e);
                    }
                    return { ...el, styles };
                });

            setElements(pageElements);
        } catch (error) {
            console.error('Erro ao carregar elementos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const rawData = e.dataTransfer.getData('element');
        if (!rawData) return; // Evitar erro se não houver dados

        let elementData;
        try {
            elementData = JSON.parse(rawData);
        } catch (err) {
            console.error("Erro ao processar elemento:", err);
            return;
        }

        // Encontrar o canvas real para calcular coordenadas relativas a ele
        const canvasElement = e.currentTarget.querySelector('.editor-canvas-container');
        if (!canvasElement) return;


        const canvasRect = canvasElement.getBoundingClientRect();
        const x = e.clientX - canvasRect.left;
        const y = e.clientY - canvasRect.top;

        const currentPage = pages[currentPageIdx];
        if (!currentPage) return;

        const newElement = {
            id: Date.now(),
            type: elementData.type,
            content: elementData.content || `Novo ${elementData.name}`,
            label: elementData.name,
            icon: elementData.icon,
            x_position: x,
            y_position: y,
            width: elementData.default_width || 150,
            height: elementData.default_height || 40,
            page_number: 1,
            section_id: currentPage.section_id,
            styles: { fontSize: 12, color: 'black' }
        };

        setElements(prev => [...prev, newElement]);
        setSelectedElementId(newElement.id);
    };

    const handleUpdateElement = (id, updates) => {
        if (updates === null) {
            setElements(elements.filter(el => el.id !== id));
            setSelectedElementId(null);
        } else {
            setElements(elements.map(el => el.id === id ? { ...el, ...updates } : el));
        }
    };

    const handleSave = async () => {
        if (!obraId) return;
        try {
            await api.put(`/maquetes/obra/${obraId}/save`, {
                elements,
                templateId: selectedTemplateId
            });
            alert('Layout da obra salvo com sucesso!');
        } catch (error) {
            console.error('Erro ao salvar:', error);
            alert('Erro ao salvar layout.');
        }
    };

    const handleCreateTemplate = async () => {
        const name = prompt('Nome do novo template:');
        if (!name) return;
        try {
            const res = await api.post('/maquetes/templates', {
                name,
                description: 'Template com capa automática'
            });
            setTemplates([...templates, res.data]);
            setSelectedTemplateId(res.data.id);
        } catch (error) {
            alert('Erro ao criar template');
        }
    };

    const handleAddPage = async () => {
        if (!availableSections.length) return;

        // Tentar encontrar uma secção genérica padrão para novas páginas
        // Prioridade: 'Página', 'Geral', 'Conteúdo' ou qualquer uma que não seja 'Capa'
        let sectionToAdd = availableSections.find(s =>
            ['Página', 'Geral', 'Conteúdo', 'Fachadas'].some(n => s.name.includes(n))
        );

        if (!sectionToAdd) {
            sectionToAdd = availableSections.find(s => s.name !== 'Capa');
        }

        // Fallback final: Primeira disponível (mesmo que seja Capa, se não houver outra)
        if (!sectionToAdd) sectionToAdd = availableSections[0];

        try {
            await api.post(`/maquetes/obra/${obraId}/add-page`, {
                sectionId: sectionToAdd.id,
                templateId: selectedTemplateId
            });
            await loadObraMaquete();
        } catch (error) {
            console.error('Erro ao adicionar página:', error);
            alert('Erro ao adicionar página');
        }
    };

    if (!obraId && obbaTheme) {
        return <MaquetesObbaDashboard obras={dashboardObras} loading={loading} error={dashboardError}
            onRetry={loadDashboard} onOpen={id => navigate(`/maquetes/${id}`)}
            onNew={() => navigate('/obras')} />;
    }
    if (!obraId) {
        return (
            <div className="maquetes-simple-page">
                <div className="top-bar">
                    <div className="top-bar-left">
                        <div className="top-bar-title">
                            <h1><LiquidText>🎨 Dashboard de Maquetas</LiquidText></h1>
                            <p>Gestão de layouts e apresentações de obra</p>
                        </div>
                    </div>
                    <div className="top-bar-actions">
                        <button className="btn-primary" onClick={() => navigate('/obras')}>
                            + Nova Maquete (Via Obra)
                        </button>
                    </div>
                </div>

                <div className="maquetes-dashboard">
                    {loading ? <p>Carregando obras...</p> : (
                        <div className="obras-grid">
                            {dashboardObras.map(obra => (
                                <div key={obra.id} className="obra-maquete-card" onClick={() => navigate(`/maquetes/${obra.id}`)}>
                                    <div className="card-header">
                                        <span className="posicao">{obra.posicao}</span>
                                        <span className={`status-tag ${obra.maquete_id ? 'has-maquete' : 'no-maquete'}`}>
                                            {obra.maquete_id ? 'Com Maquete' : 'Sem Maquete'}
                                        </span>
                                    </div>
                                    <div className="card-body">
                                        <h4>{obra.cliente}</h4>
                                        <p>{obra.descricao}</p>
                                    </div>
                                    <div className="card-footer">
                                        {obra.template_name && <small>Template: {obra.template_name}</small>}
                                        <button className="btn-edit-maquete">
                                            {obra.maquete_id ? 'Editar Maquete' : 'Criar Maquete'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="maquetes-simple-page">
            <div className="top-bar">
                <div className="top-bar-left">
                    <button className="btn-secondary" onClick={() => navigate('/maquetes')} style={{ marginRight: '15px' }}>
                        ← Voltar
                    </button>
                    <div className="top-bar-title">
                        <h1>
                            {obraData ? `${obraData.posicao} - ${obraData.descricao}` : `Editor de Maqueta - Obra ${obraId}`}
                        </h1>
                        <p>A editar layout oficial da obra</p>
                    </div>
                </div>
                <div className="top-bar-actions">
                    <button className="btn-secondary" onClick={async () => {
                        if (window.confirm('Deseja reinicializar a maquete desta obra? Isto apagará as alterações atuais.')) {
                            await api.delete(`/maquetes/obra/${obraId}/reset`);
                            loadObraMaquete();
                        }
                    }}><LiquidText>🔄 Reiniciar</LiquidText></button>
                    {selectedTemplateId && (
                        <button className="btn-primary" onClick={handleSave}><LiquidText>
                            💾 Salvar Maquete
                        </LiquidText></button>
                    )}
                </div>
            </div>

            <div className="maquetes-simple-workspace">
                <div className="left-sidebar">
                    <button className="btn-add-page" onClick={handleAddPage}>
                        <span><LiquidText>➕ Adicionar Página</LiquidText></span>
                    </button>
                    <PageThumbnails
                        pages={pages}
                        currentPage={currentPageIdx}
                        onSelectPage={setCurrentPageIdx}
                        onDeletePage={async (idx) => {
                            const pageToDelete = pages[idx];
                            try {
                                await api.delete(`/maquetes/obra/${obraId}/page/${pageToDelete.section_id}`);

                                // Ajustar índice ANTES de recarregar para evitar referências inválidas
                                if (currentPageIdx >= idx && currentPageIdx > 0) {
                                    setCurrentPageIdx(currentPageIdx - 1);
                                }

                                await loadObraMaquete();
                            } catch (error) {
                                console.error('Erro ao apagar página:', error);
                                alert('Erro ao apagar página');
                            }
                        }}
                    />
                </div>

                <div
                    className="canvas-area-simple"
                    onDrop={handleDrop}
                    onDragOver={e => e.preventDefault()}
                >
                    {loading ? <p>Carregando...</p> : (
                        <EditorCanvas
                            elements={elements.filter(el => el.section_id === pages[currentPageIdx]?.section_id)}
                            onElementChange={(id, data) => handleUpdateElement(id, data)}
                            onSelectElement={setSelectedElementId}
                            selectedElementId={selectedElementId}
                            currentPageIdx={currentPageIdx}
                            obraData={obraData}
                        />
                    )}
                </div>

                <div className="right-sidebar">
                    <ElementsLibrary onAddElement={(el) => {
                        const currentPage = pages[currentPageIdx];
                        if (!currentPage) return;

                        const newEl = {
                            ...el,
                            id: Date.now(),
                            x_position: 100,
                            y_position: 100,
                            width: el.default_width || 150,
                            height: el.default_height || 40,
                            content: el.content || `Novo ${el.name}`,
                            label: el.name,
                            section_id: currentPage.section_id,
                            page_number: 1,
                            styles: { fontSize: 12, color: 'black' }
                        };
                        setElements(prev => [...prev, newEl]);
                        setSelectedElementId(newEl.id);
                    }} />

                    <PropertiesPanel
                        element={elements.find(el => el.id === selectedElementId)}
                        onUpdate={handleUpdateElement}
                        onDelete={async (elementId) => {
                            try {
                                // Remover do estado local
                                setElements(elements.filter(el => el.id !== elementId));
                                setSelectedElementId(null);
                            } catch (error) {
                                console.error('Erro ao apagar elemento:', error);
                                alert('Erro ao apagar elemento');
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default MaquetesSimple;
